import { Fragment, useState } from "react";

import {
	closestCenter,
	DndContext,
	DragEndEvent,
	DragStartEvent,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Typography } from "@mui/material";

import { SortableContainer } from "../../../../common/DragAndDrop/SortableItem";
import { useMyCourseInstructor } from "../../hooks/useMyCourseInstructor";
import { AddLastLessonButton } from "./AddLastLessonButton";
import { CreateLessonForm } from "./CreateLessonForm";
import { ListLessonSeparator } from "./ListLessonSeparator";
import { MyCourseLesson } from "./MyCourseLesson";
import { USDFormatter } from "../../../../../utils";

export function MyCourseLessonList() {
	const { lessons, reorderLessons } = useMyCourseInstructor();

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: { distance: 10 },
		}),
	);

	const [addOrder, setAddOrder] = useState<number | null>(null);
	const [isDragging, setIsDragging] = useState(false);

	const handleDragStart = (event: DragStartEvent) => {
		setIsDragging(true);
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (over && active.id !== over.id) {
			const oldIndex = lessons.findIndex((lesson) => lesson.id === active.id);
			const newIndex = lessons.findIndex((lesson) => lesson.id === over.id);

			const newLessons = arrayMove(lessons, oldIndex, newIndex);
			reorderLessons(newLessons);
		}
		setIsDragging(false);
	};

	let lessonsString = "";
	if (lessons.length > 0) {
		if (lessons.length === 1) {
			lessonsString = "1 Class";
		} else {
			lessonsString = `${lessons.length} Classes`;
		}
		const totalCost = lessons.reduce(
			(acc, cur) => acc + (cur.totalCost ?? 0),
			0,
		);
		lessonsString = `(${lessonsString} - ${USDFormatter.format(totalCost)})`;
	}

	return (
		<>
			<Typography className="small-title-text bold">
				{`Define the scope ${lessonsString}`.trim()}
			</Typography>
			<Typography className="normal-text">
				You can include as many classes as you need, but you'll need at least 1.
				Call it the curriculum, syllabus, program, or simply the scope of your
				service.
			</Typography>
			{lessons.length === 0 && <CreateLessonForm isFirst order={1} />}
			{lessons.length > 0 && (
				<div className="item-separator-small">
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
						onDragStart={handleDragStart}>
						<SortableContext
							items={lessons}
							strategy={verticalListSortingStrategy}>
							{lessons.map((lesson, index) => (
								<Fragment key={lesson.id}>
									<ListLessonSeparator
										order={index + 1}
										addOrder={addOrder}
										setAddOrder={setAddOrder}
										isDragging={isDragging}
									/>
									<SortableContainer id={lesson.id}>
										{(setNodeRef, pointerListeners) => (
											<MyCourseLesson
												lesson={lesson}
												isDragging={isDragging}
												setNodeRef={setNodeRef}
												pointerListeners={pointerListeners}
											/>
										)}
									</SortableContainer>
								</Fragment>
							))}
						</SortableContext>
					</DndContext>
					{!isDragging && (
						<AddLastLessonButton
							order={lessons.length + 1}
							addOrder={addOrder}
							setAddOrder={setAddOrder}
						/>
					)}
				</div>
			)}
		</>
	);
}
